// `UpgradeConfirm` — modal confirming a proration upgrade (US-193).
//
// Used when the target is a higher feature tier. Displays the
// pro-rata copy + day-of-next-billing date. On confirm, calls the
// `useUpgradeSubscription` mutation; surfacing toasts is the parent's
// responsibility so the modal stays presentational.

import { useLocale, useT } from '@barel/i18n';
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@barel/ui';

import type { BillingCycle, BillingPlan, BillingPrice } from '@barel/api-client/react';

import { formatDate } from '@/lib/format';
import { formatBillingPrice } from './prices';

export interface UpgradeConfirmProps {
  open: boolean;
  targetPlan: BillingPlan | null;
  price?: BillingPrice;
  cycle?: BillingCycle;
  /** ms-epoch of next billing day. */
  nextBillingAt?: number | null;
  onConfirm: () => void;
  onClose: () => void;
  isPending?: boolean;
}

export function UpgradeConfirm({
  open,
  targetPlan,
  price,
  cycle = 'monthly',
  nextBillingAt,
  onConfirm,
  onClose,
  isPending,
}: UpgradeConfirmProps) {
  const t = useT('billing');
  const locale = useLocale();
  if (!targetPlan) return null;
  const name = t(`plans.${targetPlan}.name`);
  const formattedPrice = price ? formatBillingPrice(price, locale) : '—';
  const date = nextBillingAt
    ? formatDate(nextBillingAt, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent size="sm" data-testid="upgrade-confirm">
        <DialogHeader title={t('upgrade.title', { name })} />
        <DialogBody>
          <p className="text-sm text-secondary">
            {t(cycle === 'yearly' ? 'upgrade.bodyYearly' : 'upgrade.bodyMonthly', {
              name,
              price: formattedPrice,
              date,
            })}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} size="sm">
            {t('upgrade.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={isPending}
            disabled={!price}
            size="sm"
          >
            {t('upgrade.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
